import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  useTheme
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { useQuery } from '@tanstack/react-query';
import { loginRequest, protectedResources } from '../../authConfig';
import useFetchWithMsal from '../../hooks/useFetchWithMsal';
import InvoiceDetailsContent from '../../components/purchaseInvoices/invoiceManagement/InvoiceDetailsContent';
import { useClarificationById } from '../../hooks/purchaseInvoices/invoiceManagement/useClarification';
import InvoiceDetailsPopupStyles from '../../styles/purchaseInvoices/invoiceManagement/InvoiceDetailsPopupStyles';

const noop = () => Promise.resolve();

const ClarificationStandalonePage = () => {
  const { clarificationId } = useParams();
  const theme = useTheme();

  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [loginAttempted, setLoginAttempted] = useState(false);

  const { execute } = useFetchWithMsal({ scopes: protectedResources.apiScopes.scopes.read });

  // Trigger MSAL login if the user is not authenticated.
  // Saves the current URL first so usePostLoginRedirect can restore it after login.
  useEffect(() => {
    if (!isAuthenticated && accounts.length === 0 && inProgress === 'none' && !loginAttempted) {
      setLoginAttempted(true);
      sessionStorage.setItem(
        'redirectAfterLogin',
        window.location.pathname + window.location.search
      );
      instance.loginRedirect({ ...loginRequest, prompt: 'select_account' }).catch(() => {
        setLoginAttempted(false);
      });
    }
  }, [isAuthenticated, accounts.length, inProgress, instance, loginAttempted]);

  const { clarification, isLoading, error } = useClarificationById(
    isAuthenticated ? clarificationId : null
  );

  // Fetch approval package data so the comparison tab shows real data, not a placeholder.
  // approvalPackageId lives on the invoice subdocument — available only after clarification loads.
  // The /api/approvalPackages route is gated by PurchaseInvoicePage, which the group manager
  // has (auto-granted via the מערכות מידע role on first visit), so no extra backend bypass needed.
  const approvalPackageId = clarification?.invoiceId?.approvalPackage?._id;
  const { data: approvalPackageData, isLoading: isLoadingApprovalPackage } = useQuery({
    queryKey: ['approvalPackage', approvalPackageId],
    queryFn: async () => {
      const response = await execute(
        'GET',
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/approvalPackages/getApprovalPackageById/${approvalPackageId}`
      );
      return response?.data?.approvalPackage ?? response;
    },
    enabled: !!approvalPackageId && isAuthenticated,
    staleTime: 60 * 1000,
    retry: false
  });

  // Track whether the clarification was pending on first load.
  // Lets us distinguish "you just submitted a response" from "already answered before you arrived".
  const wasInitiallyPendingRef = useRef(null);
  if (clarification && wasInitiallyPendingRef.current === null) {
    wasInitiallyPendingRef.current = clarification.status === 'pending';
  }

  // Auth in progress or not yet started
  if (
    inProgress === 'login' ||
    inProgress === 'handleRedirect' ||
    inProgress === 'startup' ||
    (!isAuthenticated && !loginAttempted)
  ) {
    return (
      <Box sx={centered}>
        <CircularProgress size={48} />
        <Typography sx={{ mt: 2 }}>מתחבר למערכת...</Typography>
      </Box>
    );
  }

  if (!isAuthenticated) return null;

  if (isLoading) {
    return (
      <Box sx={centered}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>טוען פרטי בירור...</Typography>
      </Box>
    );
  }

  if (error) {
    const status = error?.response?.status;
    return (
      <Box sx={centered}>
        <Typography variant="h6" color="error">
          {status === 403
            ? 'אין לך הרשאה לצפות בבירור זה'
            : status === 404
            ? 'הבירור לא נמצא'
            : 'שגיאה בטעינת הבירור'}
        </Typography>
      </Box>
    );
  }

  if (!clarification) return null;

  const invoice = clarification.invoiceId; // full invoice document (populated by backend)

  if (!invoice) {
    return (
      <Box sx={centered}>
        <Typography color="text.secondary">נתוני החשבונית אינם זמינים</Typography>
      </Box>
    );
  }

  const isDone = clarification.status !== 'pending';
  const justResponded = isDone && wasInitiallyPendingRef.current === true;
  const alreadyAnswered = isDone && wasInitiallyPendingRef.current === false;

  return (
    <Box
      dir="rtl"
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#f8f9fa',
        overflow: 'hidden'
      }}
    >
      {/* Page header — mirrors InvoiceDetailsPopup's DialogTitle style */}
      <Paper
        elevation={1}
        square
        sx={{
          ...InvoiceDetailsPopupStyles.dialogTitle(theme),
          flexShrink: 0,
          borderRadius: 0
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          {`חשבונית ${invoice.booknum || invoice._id}${invoice.supName ? ` - ${invoice.supName}` : ''}`}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {invoice.companyName}
        </Typography>
      </Paper>

      {/* Banner shown after submitting — tabs remain fully visible so manager can review */}
      {justResponded && (
        <Alert
          severity="success"
          icon={<CheckCircleOutlineIcon />}
          sx={{ flexShrink: 0, borderRadius: 0, fontSize: '1rem', py: 1.5 }}
        >
          <strong>תגובתך נשלחה בהצלחה!</strong> ניתן לסגור את הדף או להמשיך לעיין בפרטי החשבונית.
        </Alert>
      )}

      {alreadyAnswered && (
        <Alert
          severity="info"
          sx={{ flexShrink: 0, borderRadius: 0, fontSize: '1rem', py: 1.5 }}
        >
          בירור זה כבר טופל בעבר. ניתן לעיין בפרטי החשבונית למטה.
        </Alert>
      )}

      {/*
        Full invoice tabs — identical set to what InvoiceDetailsPopup shows.
        With invoice.status === 'PENDING_CLARIFICATION':
          - 'details'     ✓ always
          - 'comparison'  ✓ (hasComparisonData includes PENDING_CLARIFICATION; real approvalPackageData fetched from API)
          - 'approval'    ✓ (canShowApprovalTab includes PENDING_CLARIFICATION; opens by default; ClarificationResponsePanel visible for assignee)
          - 'notes'       ✓ always
          - 'file'        ✓ always (useInvoiceFiles fetches from blob storage)
          - 'activityLog' ✓ always
      */}
      <InvoiceDetailsContent
        invoice={invoice}
        defaultActiveTabId="approval"
        clarificationOnlyMode={true}
        onSaveNotes={noop}
        onApproveInvoice={noop}
        onRejectInvoice={noop}
        approvalPackageData={approvalPackageData ?? null}
        isLoadingApprovalPackageData={isLoadingApprovalPackage}
        accountingFirm={null}
        fileAsBase64={undefined}
        isLoadingFile={false}
        initiateApproval={noop}
        allocationKeyModalOpen={false}
        setAllocationKeyModalOpen={noop}
        allocationKeys={[]}
        isLoadingAllocationKeys={false}
        allocationKeysError={null}
        currentInvoiceYear={new Date().getFullYear()}
        completeApprovalWithAllocationKey={noop}
        updateInvoiceOptimistically={noop}
        getAccountantDisplayName={undefined}
        accountantNamesCache={undefined}
      />
    </Box>
  );
};

const centered = {
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 1
};

export default ClarificationStandalonePage;
